import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DistanceBadge } from '../components/LocationManager';
import { playSuccess, playProfileVisit, playHeart, playConnectionSent } from '../utils/SoundManager';
import { VerifiedBadge, isPremiumMember } from '../components/VerifiedBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ProfileView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [message, setMessage] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [likingProfile, setLikingProfile] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);

  const getHeaders = () => {
    const uid = localStorage.getItem('userId');
    return uid ? { 'X-User-Id': uid } : {};
  };

  useEffect(() => {
    fetchProfile();
    fetchLikeStatus();
    fetchFavoriteStatus();
    fetchBlockStatus();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/profile/${userId}`,
        { withCredentials: true, headers: getHeaders() }
      );
      setProfile(response.data);
      playProfileVisit(); // صوت عند زيارة ملف شخصي
    } catch (err) {
      console.error('Error fetching profile:', err);
      setMessage('حدث خطأ في تحميل الملف الشخصي');
    } finally {
      setLoading(false);
    }
  };

  const fetchLikeStatus = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/profile/${userId}/like-status`,
        { withCredentials: true, headers: getHeaders() }
      );
      setIsLiked(response.data.liked);
    } catch (err) {
      console.log('Could not fetch like status');
    }
  };

  const fetchFavoriteStatus = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/favorites/check/${userId}`, {
        withCredentials: true, headers: getHeaders()
      });
      setIsFavorite(r.data.is_favorite);
    } catch (e) { /* ignore */ }
  };

  const fetchBlockStatus = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/blocked/check/${userId}`, {
        withCredentials: true, headers: getHeaders()
      });
      setIsBlocked(r.data.is_blocked);
    } catch (e) { /* ignore */ }
  };

  const toggleFavorite = async () => {
    try {
      const endpoint = isFavorite ? 'remove' : 'add';
      await axios.post(`${BACKEND_URL}/api/favorites/${endpoint}`, { user_id: userId }, {
        withCredentials: true, headers: getHeaders()
      });
      setIsFavorite(!isFavorite);
      if (!isFavorite) playHeart();
    } catch (e) {
      console.error(e);
    }
  };

  const toggleBlock = async () => {
    const confirmMsg = isBlocked
      ? 'هل تريد إلغاء تجاهل هذا العضو؟'
      : 'هل تريد تجاهل هذا العضو؟ لن يتمكن من التواصل معك ولن يظهر في نتائج بحثك.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const endpoint = isBlocked ? 'remove' : 'add';
      await axios.post(`${BACKEND_URL}/api/blocked/${endpoint}`, { user_id: userId }, {
        withCredentials: true, headers: getHeaders()
      });
      setIsBlocked(!isBlocked);
      if (!isBlocked) {
        // عند الحظر، نعود للوحة لأن المستخدم لم يعد يستطيع التفاعل معه
        setTimeout(() => navigate('/search'), 600);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const toggleLike = async () => {
    setLikingProfile(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/profile/${userId}/like`,
        {},
        { withCredentials: true, headers: getHeaders() }
      );
      setIsLiked(response.data.liked);
      if (response.data.liked) playHeart();
    } catch (err) {
      console.error('Error toggling like:', err);
    } finally {
      setLikingProfile(false);
    }
  };

  const sendConnectionRequest = async () => {
    setSendingRequest(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/connections/request`,
        { to_user_id: userId },
        { withCredentials: true, headers: getHeaders() }
      );
      playConnectionSent(); // صوت عند إرسال طلب تواصل
      setMessage('✅ تم إرسال طلب التواصل بنجاح!');
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.detail || 'حدث خطأ'));
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container">
        <div className="card" style={{ textAlign: 'center' }}>
          <h2>الملف الشخصي غير موجود</h2>
          <button className="btn btn-primary" onClick={() => navigate('/search')} style={{ marginTop: '20px' }}>
            العودة للبحث
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--light-bg)' }}>
      <div className="header">
        <div className="header-content">
          <h1 className="logo" onClick={() => navigate('/dashboard')} style={{cursor: 'pointer'}}>💍 وِداد</h1>
          <div className="nav-menu">
            <button className="nav-link" onClick={() => navigate('/search')}>← العودة للبحث</button>
          </div>
        </div>
      </div>

      <div className="container" style={{ paddingTop: '40px', maxWidth: '900px' }}>
        {message && (
          <div className={message.includes('✅') ? 'message message-success' : 'message message-error'}>
            {message}
          </div>
        )}

        <div className="card">
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div className="profile-avatar" style={{ width: '120px', height: '120px', fontSize: '48px', margin: '0 auto 16px' }}>
              {profile.name?.charAt(0) || '👤'}
            </div>
            <h1 style={{ 
              fontSize: '32px', 
              color: 'var(--text-dark)', 
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              flexWrap: 'wrap'
            }}>
              <span>{profile.name}</span>
              {isPremiumMember(profile) && <VerifiedBadge size="md" showText={true} />}
            </h1>
            <p style={{ fontSize: '18px', color: 'var(--text-light)', marginBottom: '12px' }}>
              {profile.age} سنة • {profile.city}
            </p>
            
            {/* عرض المسافة */}
            {profile.distance && (
              <DistanceBadge distance={profile.distance} />
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <button 
              className="btn btn-primary" 
              onClick={sendConnectionRequest}
              disabled={sendingRequest}
              style={{ flex: 1, minWidth: '180px', fontSize: '16px', padding: '16px' }}
              data-testid="send-connection-btn"
            >
              {sendingRequest ? 'جاري الإرسال...' : 'إرسال طلب تواصل'}
            </button>
            <button 
              data-testid="like-profile-btn"
              onClick={toggleLike}
              disabled={likingProfile}
              title="إعجاب"
              style={{ 
                width: '54px',
                fontSize: '24px',
                padding: '12px',
                borderRadius: '14px',
                border: isLiked ? '2px solid #e91e63' : '2px solid var(--border-color)',
                background: isLiked ? 'rgba(233, 30, 99, 0.1)' : 'var(--card-bg)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isLiked ? '❤️' : '🤍'}
            </button>
            <button 
              data-testid="favorite-profile-btn"
              onClick={toggleFavorite}
              title={isFavorite ? 'إزالة من المفضلة' : 'أضف للمفضلة'}
              style={{ 
                width: '54px',
                fontSize: '22px',
                padding: '12px',
                borderRadius: '14px',
                border: isFavorite ? '2px solid #FFA500' : '2px solid var(--border-color)',
                background: isFavorite ? 'rgba(255, 165, 0, 0.12)' : 'var(--card-bg)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isFavorite ? '⭐' : '☆'}
            </button>
            <button 
              data-testid="block-profile-btn"
              onClick={toggleBlock}
              title={isBlocked ? 'إلغاء التجاهل' : 'تجاهل هذا العضو'}
              style={{ 
                width: '54px',
                fontSize: '20px',
                padding: '12px',
                borderRadius: '14px',
                border: isBlocked ? '2px solid #888' : '2px solid var(--border-color)',
                background: isBlocked ? 'rgba(150, 150, 150, 0.15)' : 'var(--card-bg)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isBlocked ? 0.7 : 1
              }}
            >
              🚫
            </button>
          </div>

          <div className="divider"></div>

          {/* عرض نبذة عني وشريكة/شريك أحلامي */}
          {(profile.about_me || profile.ideal_partner) && (
            <>
              {profile.about_me && (
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(0, 137, 123, 0.08), rgba(0, 180, 162, 0.05))',
                  padding: '20px',
                  borderRadius: '12px',
                  marginTop: '24px'
                }}>
                  <h3 style={{ color: '#00897B', fontSize: '16px', marginBottom: '12px' }}>✨ نبذة عني</h3>
                  <p style={{ color: 'var(--text-dark)', lineHeight: '1.8', margin: 0, fontSize: '14px' }}>
                    {profile.about_me}
                  </p>
                </div>
              )}
              
              {profile.ideal_partner && (
                <div style={{ 
                  background: 'linear-gradient(135deg, rgba(255, 107, 157, 0.08), rgba(192, 110, 237, 0.05))',
                  padding: '20px',
                  borderRadius: '12px',
                  marginTop: '16px'
                }}>
                  <h3 style={{ color: 'var(--purple)', fontSize: '16px', marginBottom: '12px' }}>
                    💕 {profile.gender === 'male' ? 'شريكة أحلامي' : 'شريك أحلامي'}
                  </h3>
                  <p style={{ color: 'var(--text-dark)', lineHeight: '1.8', margin: 0, fontSize: '14px' }}>
                    {profile.ideal_partner}
                  </p>
                </div>
              )}
              
              <div className="divider" style={{ marginTop: '24px' }}></div>
            </>
          )}

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '20px', fontSize: '22px' }}>
              📋 المعلومات الأساسية
            </h3>
            
            <div className="grid grid-2" style={{ gap: '16px' }}>
              <InfoItem label="الجنس" value={profile.gender === 'male' ? 'ذكر 👨' : 'أنثى 👩'} />
              <InfoItem label="الجنسية" value={profile.nationality} />
              <InfoItem label="المدينة" value={profile.city} />
              <InfoItem label="الحي" value={profile.neighborhood} />
              {profile.has_tribe && <InfoItem label="القبيلة" value={profile.tribe_name} />}
            </div>
          </div>

          <div className="divider"></div>

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '20px', fontSize: '22px' }}>
              💼 الوظيفة والتعليم
            </h3>
            
            <div className="grid grid-2" style={{ gap: '16px' }}>
              <InfoItem label="الوظيفة" value={profile.job} />
              <InfoItem label="التخصص" value={profile.job_specialization} />
              <InfoItem label="التعليم" value={profile.education} />
              {profile.monthly_income && <InfoItem label="الدخل الشهري" value={profile.monthly_income} />}
              {profile.salary_range && profile.salary_range !== 'لاحقاً' && <InfoItem label="مقدار الراتب" value={profile.salary_range} />}
            </div>
          </div>

          <div className="divider"></div>

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '20px', fontSize: '22px' }}>
              👤 المظهر
            </h3>
            
            <div className="grid grid-2" style={{ gap: '16px' }}>
              <InfoItem label="الطول" value={`${profile.height} سم`} />
              <InfoItem label="الوزن" value={`${profile.weight} كجم`} />
              <InfoItem label="لون البشرة" value={profile.skin_color} />
              {profile.hair_length && profile.hair_length !== 'لاحقاً' && <InfoItem label="طول الشعر" value={profile.hair_length} />}
              {profile.hair_color && profile.hair_color !== 'لاحقاً' && <InfoItem label="لون الشعر" value={profile.hair_color} />}
            </div>
          </div>

          <div className="divider"></div>

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '20px', fontSize: '22px' }}>
              🕌 الالتزام الديني
            </h3>
            
            <div className="grid grid-2" style={{ gap: '16px' }}>
              <InfoItem label="الصلاة" value={profile.prayer_level} />
              {profile.gender === 'female' && profile.hijab_status && (
                <InfoItem label="الحجاب" value={profile.hijab_status} />
              )}
              <InfoItem label="الحالة الصحية" value={profile.health_status} />
            </div>
          </div>

          <div className="divider"></div>

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '20px', fontSize: '22px' }}>
              💑 الحالة الاجتماعية
            </h3>
            
            <div className="grid grid-2" style={{ gap: '16px' }}>
              <InfoItem label="الحالة" value={profile.marital_status} />
              <InfoItem label="نوع الزواج" value={profile.marriage_type} />
              <InfoItem label="يريد أطفال" value={profile.wants_children} />
              {profile.current_children > 0 && (
                <InfoItem label="عدد الأطفال" value={profile.current_children} />
              )}
            </div>
          </div>

          <div className="divider"></div>

          <div style={{ marginTop: '32px' }}>
            <h3 style={{ color: 'var(--primary)', marginBottom: '20px', fontSize: '22px' }}>
              🎯 الهوايات والاهتمامات
            </h3>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {profile.hobbies && (Array.isArray(profile.hobbies) ? profile.hobbies : profile.hobbies.split(',')).map((hobby, index) => (
                <span key={index} className="badge badge-primary" style={{ fontSize: '15px', padding: '8px 16px' }}>
                  {hobby.trim()}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  if (!value) return null;
  
  return (
    <div style={{ background: 'var(--light-bg)', padding: '16px', borderRadius: '8px' }}>
      <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ fontSize: '16px', color: 'var(--text-dark)', fontWeight: '600' }}>
        {value}
      </div>
    </div>
  );
}

export default ProfileView;
